import { FilterLiveSearch, SavedQueriesList, FilterLiveForm, useGetList } from "react-admin";
import { Autocomplete, TextField, Stack } from "@mui/material";
import { useFormContext, Controller } from "react-hook-form";
import { StyledListAside } from "@/components/ui/react-admin/StyledListAside";
import { MultiSelectFilterList } from "@/components/common/MultiSelectFilterList";
import { Reimbursement, ResourceName } from "@/types";
import { useMemo } from "react";
import { formatYearRange } from "@/utils/formatters/dateFormatter";
import { AssignmentTurnedIn as AssignmentTurnedInIcon } from "@mui/icons-material";

/** Hook to fetch all reimbursements and extract distinct reference IDs */
const useReimbursementReferenceIds = () => {
  const { data: reimbursements = [] } = useGetList<Reimbursement>(ResourceName.REIMBURSEMENTS, {
    pagination: { page: 1, perPage: 10000 },
    sort: { field: "id", order: "ASC" },
  });

  return useMemo(() => {
    const clientIds = new Set<string>();
    const claimantIds = new Set<string>();
    const policyIds = new Set<string>();

    reimbursements.forEach((r) => {
      if (r.client_id_ref) clientIds.add(r.client_id_ref);
      if (r.claimant_id_ref) claimantIds.add(r.claimant_id_ref);
      if (r.policy_id_ref) policyIds.add(r.policy_id_ref);
    });

    return { clientIds, claimantIds, policyIds };
  }, [reimbursements]);
};

const ClientFilterInput = ({ validIds }: { validIds: Set<string> }) => {
  const { control, setValue } = useFormContext();

  const { data: allClients, isLoading } = useGetList(ResourceName.CLIENTS, {
    pagination: { page: 1, perPage: 1000 },
    sort: { field: "client_legal_name", order: "ASC" },
  });

  const clients = useMemo(
    () => (allClients || []).filter((c) => validIds.size === 0 || validIds.has(c.id)),
    [allClients, validIds]
  );

  return (
    <Controller
      name="client_id_ref"
      control={control}
      render={({ field }) => (
        <Autocomplete
          options={clients}
          getOptionLabel={(option) => option.client_legal_name || ""}
          value={clients.find((c) => c.id === field.value) || null}
          onChange={(_, newValue) => {
            field.onChange(newValue?.id || null);
            setValue("claimant_id_ref", null);
            setValue("policy_id_ref", null);
          }}
          loading={isLoading}
          size="small"
          renderInput={(params) => <TextField {...params} label="Client" variant="outlined" />}
          sx={{ width: "100%" }}
        />
      )}
    />
  );
};

const ClaimantFilterInput = ({ validIds }: { validIds: Set<string> }) => {
  const { control, watch } = useFormContext();
  const selectedClientId = watch("client_id_ref");

  const { data: allClaimants, isLoading } = useGetList(ResourceName.CLAIMANTS, {
    pagination: { page: 1, perPage: 1000 },
    sort: { field: "claimant_name", order: "ASC" },
    filter: selectedClientId ? { client_id_ref: selectedClientId } : {},
  });

  const claimants = useMemo(
    () => (allClaimants || []).filter((c) => validIds.size === 0 || validIds.has(c.id)),
    [allClaimants, validIds]
  );

  return (
    <Controller
      name="claimant_id_ref"
      control={control}
      render={({ field }) => (
        <Autocomplete
          options={claimants}
          getOptionLabel={(option) => option.claimant_name || ""}
          value={claimants.find((c) => c.id === field.value) || null}
          onChange={(_, newValue) => {
            field.onChange(newValue?.id || null);
          }}
          loading={isLoading}
          size="small"
          renderInput={(params) => <TextField {...params} label="Claimant" variant="outlined" />}
          sx={{ width: "100%" }}
        />
      )}
    />
  );
};

const PolicyFilterInput = ({ validIds }: { validIds: Set<string> }) => {
  const { control, watch } = useFormContext();
  const selectedClientId = watch("client_id_ref");

  const { data: allPolicies, isLoading } = useGetList(ResourceName.POLICIES, {
    pagination: { page: 1, perPage: 1000 },
    sort: { field: "aegle_id", order: "ASC" },
    filter: selectedClientId ? { client_id_ref: selectedClientId } : {},
  });

  const policies = useMemo(
    () => (allPolicies || []).filter((p) => validIds.size === 0 || validIds.has(p.id)),
    [allPolicies, validIds]
  );

  return (
    <Controller
      name="policy_id_ref"
      control={control}
      render={({ field }) => (
        <Autocomplete
          options={policies}
          getOptionLabel={(option) => {
            const carrier =
              (option as Record<string, string>).vendor_company_name ||
              option.source_data?.stop_loss_carrier ||
              "No Carrier";
            const years = formatYearRange(option.effective_start_date, option.effective_end_date);
            return [carrier, years].filter(Boolean).join(" · ");
          }}
          value={policies.find((p) => p.id === field.value) || null}
          onChange={(_, newValue) => {
            field.onChange(newValue?.id || null);
          }}
          loading={isLoading}
          size="small"
          renderInput={(params) => <TextField {...params} label="Policy" variant="outlined" />}
          sx={{ width: "100%" }}
        />
      )}
    />
  );
};

const ReferenceFilters = () => {
  const { clientIds, claimantIds, policyIds } = useReimbursementReferenceIds();

  return (
    <Stack spacing={2}>
      <ClientFilterInput validIds={clientIds} />
      <ClaimantFilterInput validIds={claimantIds} />
      <PolicyFilterInput validIds={policyIds} />
    </Stack>
  );
};

export const ReimbursementListAside = () => {
  return (
    <StyledListAside>
      <FilterLiveSearch />
      <SavedQueriesList />

      <FilterLiveForm>
        <ReferenceFilters />
      </FilterLiveForm>

      <MultiSelectFilterList
        source="status"
        label="Status"
        icon={<AssignmentTurnedInIcon />}
        options={[
          { label: "Pending", value: "pending" },
          { label: "Approved", value: "approved" },
          { label: "Paid", value: "paid" },
          { label: "Under Review", value: "under_review" },
          { label: "Adjusted", value: "adjusted" },
          { label: "Denied", value: "denied" },
        ]}
      />
    </StyledListAside>
  );
};
